<template>
  <div class="table">
    <table class="table__content" :cols="columns.length" width="100%">
      <thead class="content__header" v-show="showColumns">
        <tr>
          <th align="center" v-if="selectable" height="50" width="20px">
            <Checkbox v-model="allSelected" size="large" />
          </th>
          <th
            height="50"
            v-for="(col, idx) in columns"
            :key="idx"
            style="font-family: Arial, Helvetica, sans-serif"
          >
            <div class="vtable__th">
              {{ col.title || col }}

              <span v-if="col.sort" class="vtable__th-sort">
                <button
                  @click="setSort(idx, true)"
                  :class="['sort__icon', { active: columnsSort[idx] === true }]"
                >
                  <svgicon icon="arrow" width="17px" />
                </button>
                <button
                  @click="setSort(idx, false)"
                  :class="[
                    'sort__icon',
                    {
                      active:
                        columnsSort[idx] === false && columnsSort[idx] !== null,
                    },
                  ]"
                >
                  <svgicon icon="arrow" width="17px" />
                </button>
              </span>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(row, i) in showItems"
          :key="i + 'tr'"
          :class="rowClasses(items[i])"
          valign="middle"
          @click="$emit('row-click', i + 1)"
        >
          <td
            align="center"
            v-if="selectable"
            @click.stop="$refs.checkboxes[i].click"
          >
            <Checkbox
              v-model="selectedBoxes[i]"
              @click.native.stop
              @input="(val) => select(val, i)"
              ref="checkboxes"
              size="large"
            />
          </td>
          <td v-for="(col, colIndex) in row" :key="colIndex + 'td'" height="37">
            <slot v-if="isOrder && colIndex === 0">
              <div class="orders-number-cell">
                <span>{{ col }}</span>
                <svgicon
                  v-if="osrIcon(items[i][0])"
                  :icon="osrIcon(items[i][0])"
                  :class="[
                    'osr-icon',
                    'osr-icon--table',
                    {
                      'osr-icon-accept': osrIcon(items[i][0]) === 'osr-accept',
                      'osr-icon-reject': osrIcon(items[i][0]) === 'osr-reject',
                    },
                  ]"
                />
              </div>
            </slot>
            <slot v-else-if="isOrder && colIndex === 1">
              <svg
                v-if="col === false"
                width="18"
                height="14"
                viewBox="0 0 18 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M2.03454 12.8333L3.19163 14L15.9195 1.16667L14.7625 0L12.787 1.99186C11.6242 1.49132 10.3443 1.21426 9 1.21426C4.90909 1.21426 1.41545 3.77988 0 7.40144C0.664162 9.10076 1.78588 10.5676 3.21215 11.646L2.03454 12.8333ZM5.42771 9.41207L6.66409 8.16545C6.58706 7.92494 6.54545 7.6682 6.54545 7.40144C6.54545 6.03201 7.64182 4.92657 9 4.92657C9.26457 4.92657 9.5192 4.96852 9.75773 5.04619L10.9941 3.79957C10.404 3.46654 9.72387 3.27666 9 3.27666C6.74182 3.27666 4.90909 5.12456 4.90909 7.40144C4.90909 8.13131 5.09741 8.8171 5.42771 9.41207Z"
                  fill="#FF0404"
                  fill-opacity="0.25"
                />
                <path
                  d="M9 13.5886C8.06171 13.5886 7.15483 13.4537 6.29725 13.2019L8.06636 11.4182C8.36627 11.4889 8.67883 11.5262 9 11.5262C11.2582 11.5262 13.0909 9.67833 13.0909 7.40144C13.0909 7.07762 13.0538 6.76246 12.9837 6.46007L15.5889 3.83333C16.6355 4.81819 17.4658 6.03461 18 7.40144C16.5845 11.023 13.0909 13.5886 9 13.5886Z"
                  fill="#FF0404"
                  fill-opacity="0.25"
                />
              </svg>
            </slot>
            <slot v-else :name="'col-' + (colIndex + 1)" :data="col">
              <div class="department-td" v-if="col.mod === 'department'">
                <div v-if="col.data.parent">
                  <p v-if="col.data.parent.parent" class="parent-department">
                    {{ col.data.parent.parent.name }}
                  </p>
                  <p class="parent-department">/{{ col.data.parent.name }}/</p>
                </div>
                {{ col.data.name }}
              </div>
              <div v-else>{{ col }}</div>
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <Preloader
      v-if="loading"
      :style="!items.length ? null : 'height: 50px; min-height: auto'"
    />

    <slot name="placeholder" v-if="!items.length && !loading" />
  </div>
</template>

<script>
import Vue from "vue";
import Preloader from "@/components/Preloader.vue";
import Checkbox from "@/components/Form/Checkbox.vue";
import { mapGetters } from "vuex";

export default Vue.extend({
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isUser: {
      type: Boolean,
      default: false,
    },
    isOrder: {
      type: Boolean,
      default: false,
    },
    isHistory: {
      type: Boolean,
      default: false,
    },
    itemsType: {
      type: String,
      default: "object",
    },
    textAlign: {
      type: String,
      default: "left",
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    sortApi: {
      type: Function,
      default: null,
    },
    selectedIndices: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    Preloader,
    Checkbox,
  },
  data() {
    return {
      allSelected: false,
      selectedBoxes: new Array(this.items.length).fill(false),
      columnsSort: this.columns.map(() => null),
    };
  },
  computed: {
    ...mapGetters("options", ["getOrdersSort"]),
    ...mapGetters("users", ["isLogist"]),
    showColumns() {
      return this.columns.length > 0;
    },
    showItems() {
      if (!this.isHistory) {
        const dataTable = this.items?.map((item) => {
          const showhItem = [];
          item.forEach((col, index) => {
            if (this.isUser || this.isOrder) {
              if (index !== 0) {
                showhItem.push(col);
              }
            } else {
              showhItem.push(col);
            }
          });
          return showhItem;
        });
        return dataTable;
      } else {
        return this.items;
      }
    },
  },
  methods: {
    async setSort(idx, sort) {
      this.columnsSort.fill(null);
      Vue.set(this.columnsSort, idx, sort);

      if (this.sortApi) {
        await this.sortApi(this.columns[idx], sort);
      }
    },
    select(value, index) {
      this.$emit("select", value, index);
    },
    rowClasses(item) {
      const meta = item[0];
      // идентификатор текущего статуса заявки
      const statusId = meta?.statusId;
      // проверка на статус «Отменена», чтобы исключить подсветку
      const isCanceled = statusId === 3;
      // статус «Завершена» отключает подсветку
      const isCompleted = statusId === 5;
      // заявка создана из другой заявки
      const hasParent = !!meta?.parentId;
      // подсветка активна только для логиста и незавершённых дочерних заявок
      const shouldHighlightLogist =
        this.isOrder &&
        this.isLogist &&
        hasParent &&
        !!statusId &&
        !isCanceled &&
        !isCompleted;
      return {
        deleted: meta !== null && this.isUser,
        changed: !!meta && meta.changed === true && this.isOrder,
        unviewed: item[2] === false,
        exchange:
          typeof item[4] === "string" &&
          (item[4]?.includes("Обмен") || item[4]?.includes("Возврат")),
        "logist-active": shouldHighlightLogist,
      };
    },
    osrIcon(meta) {
      if (!meta || !meta.osr || !meta.osr.approvable) {
        return null;
      }
      if (meta.osr.vote === true) {
        return "osr-accept";
      }
      if (meta.osr.vote === false) {
        return "osr-reject";
      }
      return "osr";
    },
  },
  watch: {
    allSelected(val) {
      if (val) {
        this.selectedBoxes = new Array(this.items.length).fill(true);
        this.$emit("select", null, null, true);
      } else if (this.selectedBoxes.every((item) => item)) {
        this.selectedBoxes = new Array(this.items.length).fill(false);
        this.$emit("select", null, null, false);
      }
    },
    selectedBoxes(val) {
      if (val.length && val.every((item) => item)) {
        this.allSelected = true;
      } else if (this.allSelected) {
        this.allSelected = false;
      }
    },
    showItems() {
      this.allSelected = false;

      const selectedBoxes = new Array(this.items.length).fill(false);
      this.selectedIndices.forEach((i) => {
        selectedBoxes[i] = true;
      });

      this.selectedBoxes = selectedBoxes;
    },
    getOrdersSort: {
      handler(newSort) {
        // Синхронизируем локальное состояние сортировки с Vuex
        if (newSort && this.isOrder) {
          this.columnsSort.fill(null);
          Vue.set(this.columnsSort, newSort.idx, newSort.flag);
        }
      },
      deep: true,
      immediate: true,
    },
  },
});
</script>

<style lang="scss">
.table {
  overflow-x: auto;
  padding-bottom: 24px;

  &::-webkit-scrollbar {
    height: 6px;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 66, 105, 0.06);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 66, 105, 0.24);
    border-radius: 4px;
  }
}

table {
  width: 100%;
  font-size: 12px !important;
}

td,
th {
  border-left: 1px solid $light-gray;
  padding: 0 15px !important;

  &:last-of-type {
    border-right: 1px solid $light-gray;
  }
}

tbody {
  tr {
    cursor: pointer;

    &:hover {
      background: rgba(33, 150, 243, 0.3) !important;
    }

    &:nth-child(odd) {
      background: #e8f2f8;
      .osr-icon {
        path:first-child {
          fill: white;
        }
      }
    }

    &.deleted {
      background: rgba(255, 68, 67, 0.6);

      &:hover {
        background: rgba(255, 68, 67, 0.3) !important;
      }
    }

    &.exchange {
      color: #ffffff;
      background: rgba(243, 139, 1, 1);

      &:hover {
        background: rgb(229, 130, 0) !important;
      }
    }
    &.logist-active {
      color: #ffffff;
      background: rgba(243, 139, 1, 1);

      &:hover {
        background: rgb(229, 130, 0) !important;
      }
    }
    &.unviewed {
      font-weight: 700;
    }
  }
}

.vtable__th {
  display: flex;
  align-items: center;

  .vtable__th-sort {
    .sort__icon {
      fill: gray;
      opacity: 0.5;
      margin: -10px 0;
      transition: none;
      height: auto !important;
      min-height: auto !important;

      &:first-of-type {
        transform: rotate(-180deg);
      }

      &.active {
        fill: $dark-blue;
        opacity: 1;
      }
    }
  }
}

.department-td {
  padding: 15px 0 !important;
  width: 250px;
}

.parent-department {
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  color: rgba(0, 32, 51, 0.6);
  margin-bottom: 6px;
}

.orders-number-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.osr-icon--table {
  height: 21px;
}
</style>
